// ============================================
// document-editor.tsx — R14 TipTap + Yjs editor
// ============================================
//
// Wires together:
//   - TipTap (ProseMirror-based) rich-text editor
//   - Yjs CRDT for collaborative state via the WebsocketProvider
//     opened by `useYjsConnection`
//   - Persisted snapshot hydration + debounced auto-save via
//     `useDocumentPersistence`
//   - `@`-mention picker fed by the parent's `useTasks` result
//
// The component deliberately does NOT own the toolbar — see
// `DocumentToolbar`. The toolbar consumes TipTap commands through
// the `getDocumentEditor(roomName)` registry exported below.

import { useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
// R20: paragraph-level text alignment (left / center / right /
// justify). Implemented inline because the standalone
// `@tiptap/extension-text-align` package is not pinned in
// `frontend/package.json`. The extension piggy-backs on the
// existing Paragraph node (added by StarterKit) and stores the
// alignment in a `textAlign` attribute that's serialised onto the
// element via the `style` attribute. The 4 toolbar buttons in
// document-toolbar.tsx call `editor.chain().focus().setTextAlign(...)
// to apply it.
import { Extension } from '@tiptap/core';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Collaboration, { isChangeOrigin } from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import { WebsocketProvider } from 'y-websocket';

// R20: declare the custom commands on TipTap's ChainedCommands so
// the toolbar buttons type-check. The actual implementations live
// in the `DocumentTextAlign`, `DocumentFontSize`, and `DocumentColor`
// extension blocks below.
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        documentTextAlign: {
            setTextAlign: (alignment: 'left' | 'center' | 'right' | 'justify' | null) => ReturnType;
        };
        documentFontSize: {
            setFontSize: (size: string | null) => ReturnType;
        };
        documentColor: {
            setColor: (color: string | null) => ReturnType;
        };
        documentHeadingId: {
            setHeadingId: (id: string | null) => ReturnType;
        };
    }
}

import { useDocumentI18n } from './document-i18n';
import {
    buildDocumentMentionExtension,
    pushMentionLoadingState,
} from './document-mention-extension';
import {
    filterMentionItems,
    membersToMentionItems,
    tasksToMentionItems,
    type DocumentMentionItem,
} from './document-mention-list';
import {
    updateDocumentMentionMembers,
    updateDocumentMentionTasks,
} from './document-mention-node-view';
import { DocumentTableShortcut } from './document-table-shortcut';
import { DocumentToc } from './document-toc';
import { DocumentTableContext } from './document-table-context-extension';
import { DocumentImage } from './document-image';
import { DocumentTableContextMenu } from './document-table-context-menu';
import { DocumentTableRowResize } from './document-table-row-resize';
import type { ProjectMember, Task } from '@/lib/schemas';
import { cn } from '@/lib/utils';

// ============================================
// TYPES
// ============================================

export interface DocumentEditorProps {
    /** Stable Yjs room name (`view:<viewId>`). */
    roomName: string;
    /**
     * R25 (Bug A): project id for the mention picker's SERVER-SIDE task
     * search. Passed straight into the mention extension so the popup
     * can query `/projects/:projectId/tasks?search=…` instead of only
     * filtering the tasks the parent already loaded.
     */
    projectId?: string;
    /** Yjs document to bind to (owned by the parent hook). */
    doc: Y.Doc;
    /** Live WebSocket provider — null for local-only (offline) mode. */
    provider: WebsocketProvider | null;
    /** Current user display name + colour for cursor presence. */
    user: { name: string; color: string };
    /**
     * Persisted snapshot (raw Yjs update bytes) to hydrate the Y.Doc
     * with before the WS sync handshake. Pass `null` for fresh docs.
     */
    snapshot: Uint8Array | null;
    /**
     * Project tasks to seed the `@`-mention picker. Re-rendered on
     * every keystroke; cheap because the picker caps at 30 entries.
     */
    mentionTasks?: Task[];
    /**
     * R16: project members to seed the `@`-mention picker. Combined
     * with `mentionTasks` so the popup shows BOTH groups, separated
     * under "Tasks" / "People" headers.
     */
    mentionMembers?: ProjectMember[];
    /** Loading state for the mention picker. */
    mentionLoading?: boolean;
    /**
     * Called when the user clicks a task mention chip in the
     * document. The parent (DocumentView) is responsible for
     * opening the task-detail side panel + closing it cleanly. We
     * deliberately don't couple the editor to navigation here so
     * the editor stays reusable.
     */
    onMentionClick?: (taskId: string) => void;
    /**
     * R16: called when the user clicks a person mention chip in the
     * document. The parent decides what to show (R25 Bug B: a popover
     * with that person's assigned tasks). `anchor` is the clicked chip
     * element, used to position the popover.
     */
    onPersonMentionClick?: (
        personId: string,
        displayName: string,
        anchor?: HTMLElement | null
    ) => void;
    /** Optional className for the editor wrapper. */
    className?: string;
    /**
     * Called once the Y.Doc is hydrated + ready. The parent uses
     * this to flip a `loading` flag in the toolbar status bar.
     */
    onReady?: () => void;
    /**
     * Called on every change with the current snapshot bytes (full
     * state vector). The parent debounces + uploads to the backend.
     */
    onSnapshotChange?: (bytes: Uint8Array) => void;
    /**
     * R20 (t62): when false, the editor renders read-only — TipTap's
     * `editable: false` blocks every keystroke + paste. The parent
     * also short-circuits snapshot uploads so a VIEWER can't even
     * fire a request.
     */
    editable?: boolean;
}

// ============================================
// HYDRATION
// ============================================
//
// We hydrate the Y.Doc with the persisted snapshot *before* the
// WebsocketProvider sync handshake so the user sees their content
// immediately. y-websocket merges any later updates on top — the
// snapshot is just a starting point, never the source of truth
// once the WS is connected.
//
// The hydration is a no-op when `snapshot` is null/empty (fresh doc).

function hydrateDoc(doc: Y.Doc, snapshot: Uint8Array | null) {
    if (!snapshot || snapshot.byteLength === 0) return;
    Y.applyUpdate(doc, snapshot);
}

// ============================================
// TEXT ALIGNMENT EXTENSION (R20)
// ============================================
//
// A minimal paragraph-level text-alignment extension that doesn't
// pull in the standalone `@tiptap/extension-text-align` package.
// We add a `textAlign` attribute to the Paragraph node (added by
// StarterKit) and a few commands the toolbar calls into.
//
// We use CSS values for the attribute (left / center / right /
// justify) so the rendered HTML stays readable in non-TipTap
// contexts (PDF export, plain copy/paste). `null` means "inherit
// the body's default" — we DON'T render a `style` attribute when
// the value is null, so default alignment stays unstyled.

const TEXT_ALIGN_VALUES = ['left', 'center', 'right', 'justify'] as const;
type TextAlignValue = (typeof TEXT_ALIGN_VALUES)[number] | null;

const DocumentTextAlign = Extension.create({
    name: 'documentTextAlign',

    addGlobalAttributes() {
        return [
            {
                types: ['paragraph', 'heading'],
                attributes: {
                    textAlign: {
                        default: null,
                        parseHTML: (element: HTMLElement) => {
                            const v = element.style.textAlign;
                            return v && (TEXT_ALIGN_VALUES as readonly string[]).includes(v)
                                ? (v as TextAlignValue)
                                : null;
                        },
                        renderHTML: (attributes: { textAlign?: string | null }) => {
                            if (!attributes.textAlign) return {};
                            return { style: `text-align: ${attributes.textAlign}` };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setTextAlign:
                (alignment: TextAlignValue) =>
                ({ commands }: { commands: { updateAttributes: (name: string, attrs: Record<string, unknown>) => boolean } }) => {
                    if (alignment === null) {
                        return commands.updateAttributes('paragraph', { textAlign: null });
                    }
                    return commands.updateAttributes('paragraph', { textAlign: alignment });
                },
        };
    },
});

// ============================================
// FONT SIZE EXTENSION (R20)
// ============================================
//
// Custom font-size extension because `@tiptap/extension-font-size`
// is not pinned in `frontend/package.json`. The extension adds a
// `fontSize` attribute to the TextStyle mark so the toolbar's
// font-size selector + custom-size input actually apply. The
// previous code called `setMark('textStyle', { fontSize: value })`
// which silently dropped the unknown attribute — that's why the
// font-size Select "didn't work" for the user.
//
// The attribute is rendered as `style="font-size: …"` on the mark
// span. Inline styles beat Tailwind Typography's
// `.prose { font-size: … }` rule.

interface FontSizeOptions {
    /** Default font-size (CSS string, e.g. "1rem"). */
    defaultSize?: string;
}

const DocumentFontSize = Extension.create<FontSizeOptions>({
    name: 'documentFontSize',

    addOptions() {
        return {
            defaultSize: '1rem',
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: ['textStyle'],
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (element: HTMLElement) => element.style.fontSize || null,
                        renderHTML: (attributes: { fontSize?: string | null }) => {
                            if (!attributes.fontSize) return {};
                            return { style: `font-size: ${attributes.fontSize}` };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setFontSize:
                (size: string | null) =>
                ({ commands }: { commands: { setMark: (name: string, attrs: Record<string, unknown>) => boolean } }) => {
                    return commands.setMark('textStyle', { fontSize: size });
                },
        };
    },
});

// ============================================
// TEXT COLOR EXTENSION (R20)
// ============================================
//
// Custom text-colour extension because `@tiptap/extension-color`
// is not pinned in `frontend/package.json`. Adds a `color`
// attribute to the TextStyle mark. The toolbar renders a colour
// swatch palette (8 preset colours + a native picker for custom
// values); the command accepts either a hex string (`#ff0000`)
// or `null` (clears the colour).

interface DocumentColorOptions {
    /** Default colour when nothing is set. */
    defaultColor?: string | null;
}

const DocumentColor = Extension.create<DocumentColorOptions>({
    name: 'documentColor',

    addOptions() {
        return {
            defaultColor: null,
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: ['textStyle'],
                attributes: {
                    color: {
                        default: null,
                        parseHTML: (element: HTMLElement) => element.style.color || null,
                        renderHTML: (attributes: { color?: string | null }) => {
                            if (!attributes.color) return {};
                            return { style: `color: ${attributes.color}` };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setColor:
                (color: string | null) =>
                ({ commands }: { commands: { setMark: (name: string, attrs: Record<string, unknown>) => boolean } }) => {
                    return commands.setMark('textStyle', { color });
                },
        };
    },
});

// ============================================
// HEADING ID EXTENSION (R20)
// ============================================
//
// Adds an `id` attribute to heading nodes so the Table of
// Contents can link to each heading via `#<id>` anchor — and so
// the exported PDF's `enableLinks: true` keeps those anchors
// clickable across pages.
//
// We auto-generate a slug from the heading text on the first
// commit (or whenever the heading becomes empty). The slug is
// deterministic enough that the TOC outline stays stable across
// edits — re-slugging happens only when the heading text changes.

function slugify(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .slice(0, 64) || 'section';
}

// Stable identity and duplicate repair are shared with export fixtures.
import { DocumentHeadingId } from './document-heading-id';

// ============================================
// TABLE OF CONTENTS HELPERS (R20)
// ============================================
//
// Lightweight TOC builder that walks the editor's document and
// returns a flat list of `{ id, level, text }` entries. The
// toolbar renders these in a Popover dropdown; clicking an
// entry scrolls the editor to that heading and inserts an
// anchor-friendly id when one is missing.

export interface DocumentTocEntry {
    id: string;
    level: 1 | 2 | 3;
    text: string;
}

/**
 * Walk the editor's document and collect every heading node.
 * Headings nested inside tables / nested lists are skipped —
 * we only want top-level section markers.
 */
export function buildDocumentToc(editor: Editor): DocumentTocEntry[] {
    if (!editor || editor.isDestroyed) return [];
    const entries: DocumentTocEntry[] = [];
    type DocNode = {
        isTextblock: boolean;
        type: { name: string };
        attrs: Record<string, unknown>;
        textContent: string;
    };
    type DocParent = { type: { name: string } } | null;
    editor.state.doc.descendants((
        node: DocNode,
        _pos: number,
        parent: DocParent
    ) => {
        if (!node.isTextblock || node.type.name !== 'heading') return true;
        if (parent && parent.type.name === 'tableCell') return false;
        const level = (node.attrs.level as 1 | 2 | 3) ?? 1;
        if (level < 1 || level > 3) return true;
        const text = node.textContent;
        let id = (node.attrs.id as string | null | undefined) ?? null;
        if (!id) {
            id = slugify(text);
        }
        entries.push({ id, level, text });
        return true;
    });
    return entries;
}

// ============================================
// COMPONENT
// ============================================

const FONT_FAMILY_OPTIONS: Record<string, string> = {
    sans: 'Inter, ui-sans-serif, system-ui, sans-serif',
    serif: 'Georgia, ui-serif, serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const FONT_SIZE_OPTIONS: Record<string, string> = {
    small: '0.875rem',
    medium: '1rem',
    large: '1.25rem',
};

export function DocumentEditor({
    roomName,
    projectId,
    doc,
    provider,
    user,
    snapshot,
    className,
    mentionTasks,
    mentionMembers,
    mentionLoading,
    onMentionClick,
    onPersonMentionClick,
    editable = true,
    onReady,
    onSnapshotChange,
}: DocumentEditorProps) {
    const { t, language } = useDocumentI18n();
    const languageRef = useRef(language);
    languageRef.current = language;
    const [ready, setReady] = useState(false);

    // ------------------------------------------------------------------
    // Mention picker wiring
    // ------------------------------------------------------------------
    // R20 round 5 — Bug 5 fix (REAL ROOT CAUSE):
    //
    // The mention extension was previously built via `useMemo` keyed
    // on `[mentionTasks, mentionMembers]`. When mentionTasks
    // resolved from the React Query (25 items after the initial
    // empty placeholder), `mentionExtension` was recreated — but
    // the editor still held the OLD reference because useEditor's
    // deps `[roomName, doc, provider]` hadn't changed. The result:
    // the picker always showed the items from the FIRST render
    // (empty), so the user saw "no data" even though the toolbar's
    // PDF export showed `tasks= 25`.
    //
    // The fix: build the mention extension ONCE per editor mount,
    // but have its `getItems` + `isLoading` callbacks read from
    // refs that are mutated on every render. The picker always
    // reads the LATEST items without us having to recreate the
    // editor (which would tear down ProseMirror + lose undo
    // history).
    const mentionItemsRef = useRef<DocumentMentionItem[]>([]);
    mentionItemsRef.current = useMemo(
        () => [
            ...tasksToMentionItems(mentionTasks ?? []),
            ...membersToMentionItems(mentionMembers ?? []),
        ],
        [mentionTasks, mentionMembers]
    );
    const mentionLoadingRef = useRef(false);
    mentionLoadingRef.current = !!mentionLoading;

    const mentionExtension = useMemo(
        () =>
            buildDocumentMentionExtension({
                // R25 (Bug A): projectId enables the picker's debounced
                // server-side task search.
                projectId,
                getItems: (query: string) =>
                    filterMentionItems(mentionItemsRef.current, query),
                isLoading: () => mentionLoadingRef.current,
                onTaskMentionClick: onMentionClick,
                onPersonMentionClick,
            }),
        // Stable — the closures read from refs so this extension
        // never needs to be rebuilt. We deliberately OMIT
        // `mentionTasks` / `mentionMembers` / `mentionLoading`
        // from the deps: they're captured via refs.
        // `projectId` IS a dep: if it changes (project switch) the
        // roomName changes too, which recreates the editor — the
        // extension must then be rebuilt with the new project.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onMentionClick, onPersonMentionClick, projectId]
    );

    // Push the latest task list into the NodeView lookup registry on
    // every change. The NodeView reads this synchronously when
    // rendering so the chip's status badge stays in sync with the
    // task list refresh (after a rename, after a status change, etc.).
    useEffect(() => {
        updateDocumentMentionTasks(mentionTasks);
    }, [mentionTasks]);

    // R16: mirror the latest project members list into the NodeView
    // lookup registry so the person chip can render its Avatar with
    // the right image + initials.
    useEffect(() => {
        updateDocumentMentionMembers(mentionMembers);
    }, [mentionMembers]);

    // R17 (t56): push the live `isLoading` state to the active
    // mention picker so the "Loading tasks…" indicator clears the
    // moment React Query finishes loading — even if the user isn't
    // typing into the trigger. The mention plugin only calls
    // `component.updateProps()` from `onUpdate` (trigger keystroke),
    // so without this side-channel the picker would stay stuck on
    // its initial snapshot of `isLoading: true`.
    useEffect(() => {
        pushMentionLoadingState(!!mentionLoading);
    }, [mentionLoading]);

    // ----------------------------------------------------------------------
    // Hydrate the Y.Doc with the persisted snapshot BEFORE the
    // WebsocketProvider connects. This ensures the user sees their
    // last-known content immediately instead of a flash of empty doc.
    //
    // R18 (t59): the previous code was guarded by a single
    // `hydratedRef.current = true` that flipped on the FIRST render
    // and stayed true forever. When the user switched tabs the parent
    // (`document-view.tsx`) passed a NEW `doc` (from
    // `useYjsConnection`'s `useMemo([viewId])`) AND a NEW `snapshot`
    // (from `useDocumentSnapshot(activeViewId)`), but the stale
    // hydratedRef blocked both — the new Y.Doc stayed empty and the
    // persisted snapshot was never applied. The user reported
    // "always the same content" because the WS server often hasn't
    // received a CRDT update for the new room yet, so the live sync
    // stream arrives after the user has already given up.
    //
    // We now hydrate whenever EITHER the Y.Doc instance OR the
    // snapshot bytes change. Applying a snapshot to a freshly-bound
    // Y.Doc is idempotent (Y.applyUpdate is a CRDT merge), so
    // re-hydrating on snapshot reload is safe.
    //
    // R19 (t60) — bug 1 fix: the R18 hydration ran DURING render
    // (an anti-pattern in React 18 StrictMode, which double-
    // invokes renders and can re-apply a snapshot twice). We now
    // run it inside a useEffect that fires after React commits
    // the new doc/snapshot prop values. The ref still tracks
    // { doc, snapshot } so we only re-apply on a real change.
    //
    // ALSO: the new useEffect runs on EVERY { doc, snapshot } pair
    // change — including the FIRST pair after DocumentEditor
    // mounts. That's what the user reported as "always the same
    // content": the previous R18 fix depended on the editor being
    // re-created on `roomName` change but the new Y.Doc was
    // arriving BEFORE the new snapshot loaded. The Y.Doc sat
    // empty until the WS handshake, so the user gave up and
    // typed, and the WS handed them what looked like "the same"
    // empty cell. By running hydration as a proper effect we
    // guarantee the sequence is:
    //   1. doc changes (new useMemo result)
    //   2. snapshot changes (from useDocumentSnapshot load)
    //   3. useEffect runs, applies snapshot, empty Y.Doc becomes
    //      the persisted snapshot
    //   4. WS connects, syncs any missed updates
    // ----------------------------------------------------------------------
    const hydratedForRef = useRef<{
        doc: Y.Doc;
        snapshot: Uint8Array | null;
    } | null>(null);
    useEffect(() => {
        const isHydrated =
            hydratedForRef.current !== null &&
            hydratedForRef.current.doc === doc &&
            hydratedForRef.current.snapshot === snapshot;
        if (isHydrated) return;
        hydrateDoc(doc, snapshot);
        hydratedForRef.current = { doc, snapshot };
    }, [doc, snapshot]);

    const editor = useEditor(
        {
            // R20 (t62): VIEWERs render the editor read-only so
            // TipTap refuses every keystroke / paste. The parent also
            // short-circuits snapshot uploads so a VIEWER can't even
            // fire a request to the backend.
            editable,
            // We intentionally leave `extensions` empty here and add
            // them inside the useEffect below. TipTap's `useEditor`
            // requires extensions synchronously, so we pre-build the
            // array in the useEffect setup and recreate the editor
            // when the roomName changes.
            extensions: [
                StarterKit.configure({
                    // Disable StarterKit's history extension — the
                    // Yjs Collaboration extension owns undo/redo via
                    // Y.UndoManager. Keeping StarterKit's history on
                    // would cause double-tracked operations and break
                    // the collab cursor.
                    history: false,
                }),
                DocumentImage,
                Underline,
                TextStyle,
                FontFamily.configure({
                    types: ['textStyle'],
                }),
                // R20 — text alignment (left / center / right / justify).
                // See the DocumentTextAlign definition at the top of
                // this file. Adds a paragraph-level `textAlign`
                // attribute consumed by the 4 toolbar buttons.
                DocumentTextAlign,
                // R20 — custom fontSize + color on the TextStyle
                // mark. Without these, the font-size selector and
                // the colour picker silently drop their values
                // because the TextStyle mark schema doesn't
                // declare `fontSize` / `color` attributes.
                DocumentFontSize,
                DocumentColor,
                // R20 — heading id (used by Table of Contents + PDF
                // anchor links).
                DocumentHeadingId,
                DocumentToc.configure({ getLanguage: () => languageRef.current }),
                DocumentTableContext,
                Table.configure({
                    resizable: true,
                    HTMLAttributes: {
                        class: 'doc-editor-table',
                    },
                }),
                TableRow,
                TableHeader,
                TableCell,
                DocumentTableRowResize,
                // R20: converts a typed `+table=rows x cols` token
                // (e.g. "+table=3x4") into a freshly inserted table
                // when the user follows it with a space. The
                // shortcut is intentionally explicit so it doesn't
                // fire on prose mentions like "3x4 profit". See
                // document-table-shortcut.ts for the bounds check
                // (clamped to [1, 20] so the editor stays
                // responsive).
                DocumentTableShortcut,
                // R14: @-mention picker. The extension mounts a
                // tippy.js popover whose content is
                // DocumentMentionList. The mention node renders as
                // an inline chip via `addNodeView` below.
                mentionExtension,
                // Collaboration binds the ProseMirror state to the
                // Y.Doc. CRDT conflicts are auto-resolved; the
                // editor never throws "concurrent edit" errors.
                ...(provider
                    ? [
                        Collaboration.configure({ document: doc, field: 'prosemirror' }),
                        CollaborationCursor.configure({
                            provider,
                            user: { name: user.name, color: user.color },
                        }),
                    ]
                    : [Collaboration.configure({ document: doc, field: 'prosemirror' })]),
            ],
            editorProps: {
                attributes: {
                    class: cn(
                        // R14 responsive: smaller padding on mobile
                        // so the editor viewport fits a 360px screen
                        // without horizontal page-level overflow.
                        'doc-editor-content prose prose-invert max-w-none focus:outline-none min-h-[60vh] px-4 py-3 sm:px-6 sm:py-4',
                        className
                    ),
                    'data-testid': 'document-editor-content',
                    'aria-label': t('document.editorPlaceholder'),
                    // R20: surfaces the `+table=3x4 + space` shortcut
                    // hint via the existing placeholder pseudo-
                    // element (see `p.is-editor-empty:first-child::
                    // before` in index.css). We append the hint so
                    // the primary CTA stays on top.
                    'data-placeholder': t('document.editorPlaceholder') + '\n' + t('document.tableHint'),
                },
            },
            onUpdate: ({ editor: ed, transaction }) => {
                // The Yjs Collaboration extension is authoritative
                // for state — we don't ship ProseMirror JSON to the
                // backend. We just serialize the Y.Doc state vector
                // and hand it to the parent's debounced uploader.
                if (!onSnapshotChange) return;
                // R26: only LOCAL edits schedule an upload. Transactions
                // that carry the y-sync meta come from Yjs itself —
                // the initial snapshot/websocket hydration and remote
                // peers' edits. Encoding the whole Y.Doc for those was
                // pure CPU waste (it is O(document size) per update) and
                // produced redundant uploads of content that is already
                // persisted by whoever authored it.
                if (isChangeOrigin(transaction)) return;
                try {
                    const bytes = Y.encodeStateAsUpdate(doc);
                    onSnapshotChange(bytes);
                } catch (err) {
                    console.warn('[DocumentEditor] snapshot encode failed', err);
                }
                // Avoid unused-var lint: ed is only here for clarity
                // (we could also pull text length if we want a
                // "characters" counter later).
                void ed;
            },
            immediatelyRender: false,
        },
        // Re-create the editor when the room, Y.Doc, or provider
        // changes.
        //
        // R20 (Bug 1 / Bug 5 fix): the previous version only listed
        // `[roomName]` to avoid throwing away the editor on transient
        // WS reconnects. That left a stale `provider` baked into the
        // CollaborationCursor extension — the closure captured the
        // provider from the render where viewId just changed (the
        // useEffect that creates the new provider hadn't run yet),
        // so the cursor extension listened to the OLD (now destroyed)
        // provider's awareness events. Other users' cursors never
        // arrived because the awareness subscription was bound to a
        // dead socket.
        //
        // Adding `provider` to deps makes the editor recreate once
        // more (right after the new provider lands in state) — that's
        // the recolor where CollaborationCursor receives the live
        // provider. The cost is one extra recreation on view switch
        // (and on transient reconnects that swap the provider object);
        // the Y.Doc reference is stable across the recreation so the
        // document content survives intact.
        [roomName, doc, provider]
    );

    useEffect(() => {
        // Presentation-only refresh; does not change the Yjs document.
        if (editor && !editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta('tocLocale', language));
    }, [editor, language]);

    // Mark the editor as ready once mounted so the toolbar can show
    // the live status.
    useEffect(() => {
        if (!editor) return;
        setReady(true);
        onReady?.();
        return () => {
            setReady(false);
        };
    }, [editor, onReady]);

    // Wire the mention click handlers onto the editor's storage so
    // the NodeView (which lives outside React's render tree) can pull
    // them via `editor.storage.documentMentionClick(id)` /
    // `editor.storage.documentPersonMentionClick(id, label)` without
    // needing a context boundary. We update on every prop change so a
    // stale callback can't outlive the parent.
    useEffect(() => {
        if (!editor) return;
        const storage = editor.storage as {
            documentMentionClick: ((id: string) => void) | null;
            documentPersonMentionClick:
                | ((
                      id: string,
                      label: string,
                      anchor?: HTMLElement | null
                  ) => void)
                | null;
        };
        storage.documentMentionClick = onMentionClick ?? null;
        storage.documentPersonMentionClick = onPersonMentionClick ?? null;
    }, [editor, onMentionClick, onPersonMentionClick]);

    // Expose the editor instance on a singleton so the toolbar can
    // grab it from outside the React tree. (We deliberately don't
    // lift the editor state into a context — only the toolbar needs
    // imperative access, and keeping the API minimal simplifies
    // future refactors.)
    useEffect(() => {
        if (editor) {
            (DocumentEditorRegistry.current = DocumentEditorRegistry.current || {} as Record<string, unknown>);
            DocumentEditorRegistry.current[roomName] = editor;
        }
        return () => {
            if (DocumentEditorRegistry.current) {
                delete DocumentEditorRegistry.current[roomName];
            }
        };
    }, [editor, roomName]);

    if (!editor) {
        return (
            <div className="flex items-center justify-center py-12 text-sm text-foreground-muted">
                {t('document.loading')}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'rounded-md border border-border bg-surface/40',
                !ready && 'opacity-60'
            )}
            data-testid="document-editor"
        >
            <EditorContent editor={editor} />
            {editable && <DocumentTableContextMenu editor={editor} />}
        </div>
    );
}

// ============================================
// EDITOR REGISTRY
// ============================================
//
// Lightweight imperative bridge between the toolbar and the editor.
// The toolbar renders OUTSIDE the editor's React tree (in
// DocumentView), so it can't access the editor via hooks. Instead,
// DocumentEditor stores its instance in a module-scope registry keyed
// by roomName. The toolbar grabs the right one via
// `getDocumentEditor(roomName)`.
//
// This is intentionally simpler than building a full context — the
// document mode is only mounted when the user is actively writing,
// and there's exactly one editor per room.

const DocumentEditorRegistry: { current: Record<string, unknown> | null } = { current: null };

export function getDocumentEditor(roomName: string): import('@tiptap/react').Editor | null {
    const reg = DocumentEditorRegistry.current;
    if (!reg) return null;
    const editor = reg[roomName];
    return (editor as import('@tiptap/react').Editor) || null;
}

// ============================================
// PUBLIC HELPERS (re-used by the toolbar)
// ============================================

export const DOCUMENT_FONT_FAMILY_OPTIONS = FONT_FAMILY_OPTIONS;
export const DOCUMENT_FONT_SIZE_OPTIONS = FONT_SIZE_OPTIONS;